import { useMemo, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { useMsal } from '@azure/msal-react'
import axios from 'axios'
import clsx from 'clsx'
import { loginRequest } from './msalConfig'
import { useLocalAuth } from './LocalAuthContext'
import type { LucideIcon } from 'lucide-react'
import {
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  Mail,
  Shield,
  Smartphone,
} from 'lucide-react'
import { passkeysSupported } from '@/auth/passkeys'
import TotpEnrolmentFlow from '@/components/settings/TotpEnrolmentFlow'
import { enrolmentClient } from '@/components/settings/totpClient'
import { useClientProfile } from '@/hooks/useClientProfile'
import { apiErrorText } from '@/utils/apiErrorText'

const securitySignals: Array<{ label: string; icon: LucideIcon }> = [
  { label: 'AES-256 Encrypted', icon: Lock },
  { label: 'Protected Access', icon: Shield },
  { label: 'Session Controlled', icon: KeyRound },
]

type EmailStep = 'login' | '2fa' | 'enrol'

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'
/** How a rejected sign-in field is named when the server says it failed validation. */
const LOGIN_FIELDS = { email: 'Email', password: 'Password', code: 'Code' }

const SHOW_MICROSOFT_LOGIN_UI = false

type InputFieldProps = {
  autoComplete?: string
  autoFocus?: boolean
  icon: LucideIcon
  id: string
  inputMode?: 'email' | 'numeric' | 'text'
  label: string
  maxLength?: number
  onChange: (value: string) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  placeholder: string
  rightSlot?: ReactNode
  type?: 'email' | 'password' | 'text'
  value: string
}

function InputField({
  autoComplete,
  autoFocus,
  icon: Icon,
  id,
  inputMode,
  label,
  maxLength,
  onChange,
  onKeyDown,
  placeholder,
  rightSlot,
  type = 'text',
  value,
}: InputFieldProps) {
  const isCodeField = inputMode === 'numeric'

  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-[11px] font-medium uppercase tracking-[0.28em] text-slate-400">{label}</label>
      <div className="relative">
        {!isCodeField && <Icon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />}
        <input
          id={id}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          inputMode={inputMode}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          type={type}
          value={value}
          className={clsx(
            'login-field',
            rightSlot ? 'pr-12' : '',
            isCodeField ? 'px-6 text-center text-2xl tracking-[0.42em]' : ''
          )}
        />
        {rightSlot ? <div className="absolute inset-y-0 right-4 flex items-center">{rightSlot}</div> : null}
      </div>
    </div>
  )
}

function SecurityStrip() {
  return (
    <div className="flex items-center justify-center gap-2.5">
      {securitySignals.map(({ label, icon: Icon }) => (
        <div key={label} title={label} role="img" aria-label={label} className="login-security-icon">
          <Icon size={13} />
        </div>
      ))}
    </div>
  )
}

export default function LoginPage() {
  const client = useClientProfile()
  const { instance } = useMsal()
  const { login, loginWithPasskey, verify2FA, finishEnrolment } = useLocalAuth()

  const [emailStep, setEmailStep] = useState<EmailStep>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [partialToken, setPartialToken] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleMicrosoftLogin = () => {
    instance.loginRedirect(loginRequest)
  }

  const handlePasskeyLogin = async () => {
    if (!email.trim()) {
      setError('Enter your email, then use passkey sign-in.')
      return
    }
    setError('')
    setLoading(true)
    try {
      await loginWithPasskey(email.trim())
    } catch (err: unknown) {
      setError(apiErrorText(err, err instanceof Error ? err.message : 'Passkey sign-in failed.', LOGIN_FIELDS))
    } finally {
      setLoading(false)
    }
  }

  const handleEmailLogin = async () => {
    setError('')
    setLoading(true)
    try {
      const result = await login(email, password)
      if (result.requires_2fa) {
        setPartialToken(result.token)
        setEmailStep('2fa')
      } else if (result.requires_2fa_enrolment) {
        setPartialToken(result.token)
        setEmailStep('enrol')
      }
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Login failed. Check your credentials.', LOGIN_FIELDS))
    } finally {
      setLoading(false)
    }
  }

  // The enrolment token goes in an explicit header on bare axios: nothing is
  // stored yet, and the app's own instance would swap in a stored session.
  const enrolClient = useMemo(
    () =>
      enrolmentClient(axios, API_BASE, partialToken ? { Authorization: `Bearer ${partialToken}` } : undefined),
    [partialToken],
  )

  const handle2FAVerify = async () => {
    setError('')
    setLoading(true)
    try {
      await verify2FA(totpCode, partialToken)
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Invalid 2FA code.', LOGIN_FIELDS))
    } finally {
      setLoading(false)
    }
  }

  const renderError = () => (
    error ? (
      <div className="flex items-start gap-2 rounded-[20px] border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    ) : null
  )

  const renderActionLabel = (label: string, icon: LucideIcon) => {
    const Icon = icon

    if (loading) {
      return <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/25 border-t-white" />
    }

    return (
      <>
        <Icon size={16} className="login-action-icon" />
        {label}
      </>
    )
  }

  return (
    <div className="relative min-h-[100dvh] w-screen overflow-x-hidden overflow-y-auto bg-[#0a0a0a] text-white">
      <div className="absolute inset-0 isolate overflow-hidden">
        <video
          autoPlay
          className="absolute inset-0 h-full w-full object-cover object-[14%_74%]"
          loop
          muted
          playsInline
          poster="/login/login-background-poster.jpg"
          preload="auto"
        >
          <source src="/login/helios-login-bg.mp4" type="video/mp4" />
        </video>
        {/* Tints the video toward brand gold (source footage reads cool/blue). */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[#c9a43d] mix-blend-color"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(180deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:80px_80px] opacity-[0.05]" />
      </div>

      <div className="relative z-10 flex min-h-[100dvh] w-full items-start justify-center px-4 py-4 sm:px-6 sm:py-6 lg:items-center lg:justify-end lg:px-8 lg:py-6 lg:pr-10 xl:pr-14 2xl:pr-20 overflow-y-auto">
        <div className="login-dialog w-full max-w-[370px] lg:max-w-[350px] my-auto max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <div className="px-5 py-4 sm:px-6 sm:py-4">
            <div className="flex justify-center">
              <div className="h-[40px] w-[130px] overflow-hidden sm:h-[45px] sm:w-[145px]">
                <img
                  alt="Helios FinTech"
                  className="h-full w-full scale-[1.65] object-contain"
                  src="/branding/helios-login-logo.svg"
                />
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="flex items-center justify-center gap-1.5 whitespace-nowrap text-center sm:gap-2">
                <div className="text-[7px] font-medium uppercase tracking-[0.24em] text-[#d9b768] sm:text-[8px] sm:tracking-[0.32em]">
                  Capital Markets OS
                </div>
                <img
                  alt="CRE Finance Division"
                  className="h-4 w-4 object-contain sm:h-[18px] sm:w-[18px]"
                  src="/branding/helios-cre-financing-division.svg"
                />
                <div className="text-[7px] font-medium uppercase tracking-[0.2em] text-[#f6df9a] sm:text-[8px] sm:tracking-[0.26em]">
                  CRE Finance Division
                </div>
              </div>
              {client.logoLight ? (
                <div className="mt-4 flex justify-center">
                  <div className="flex h-[32px] w-[130px] items-center justify-center sm:h-[36px] sm:w-[145px]">
                    <img
                      alt={client.name}
                      className="h-full w-full object-contain object-center"
                      src={client.logoLight}
                    />
                  </div>
                </div>
              ) : null}
              {client.name ? (
                <div className="mt-1 text-[8px] font-medium uppercase tracking-[0.2em] text-neutral-400">
                  Customized for <span className="text-[#d9b768]">{client.name}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="px-5 pb-5 pt-1 sm:px-6 sm:pb-6 sm:pt-2">
            <section className="mx-auto w-full max-w-[320px]">
              <div className="mt-1">
                  {emailStep === 'login' && (
                    <div className="login-panel">
                      <div className="space-y-3.5">
                        <InputField
                          autoComplete="email"
                          icon={Mail}
                          id="login-email"
                          inputMode="email"
                          label="Email"
                          onChange={setEmail}
                          onKeyDown={(event) => event.key === 'Enter' && handleEmailLogin()}
                          placeholder="Enter your email address"
                          type="email"
                          value={email}
                        />
                        <InputField
                          autoComplete="current-password"
                          icon={Lock}
                          id="login-password"
                          label="Password"
                          onChange={setPassword}
                          onKeyDown={(event) => event.key === 'Enter' && handleEmailLogin()}
                          placeholder="Enter your password"
                          rightSlot={(
                            <button
                              className="rounded-full p-1 text-neutral-500 transition-colors hover:text-amber-200"
                              onClick={() => setShowPassword((current) => !current)}
                              type="button"
                            >
                              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          )}
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                        />

                        {renderError()}

                        <div className="flex flex-col items-center gap-2.5 pt-1">
                          <button
                            className="login-action-gold"
                            disabled={loading || !email || !password}
                            onClick={handleEmailLogin}
                            type="button"
                          >
                            {renderActionLabel('Log in with password', KeyRound)}
                          </button>

                          {passkeysSupported() ? (
                            <button
                              className="login-action-gold login-action-gold-secondary"
                              disabled={loading || !email.trim()}
                              onClick={() => void handlePasskeyLogin()}
                              type="button"
                            >
                              {renderActionLabel('Sign in with passkey', Shield)}
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <div className="mt-5">
                        <SecurityStrip />
                      </div>
                    </div>
                  )}

                  {emailStep === 'enrol' && (
                    <div className="login-panel">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-100">
                          <Smartphone size={22} />
                        </div>
                        <div>
                          <div className="text-[11px] font-medium uppercase tracking-[0.34em] text-slate-400">Two-Factor Auth</div>
                          <h2 className="mt-1 text-xl font-medium text-white">Set up two-factor authentication</h2>
                        </div>
                      </div>

                      <div className="mt-4">
                        <TotpEnrolmentFlow
                          autoBegin
                          buttonClassName="login-action-gold disabled:opacity-50"
                          client={enrolClient}
                          inputClassName="login-field px-6 text-center text-2xl tracking-[0.42em]"
                          intro="Your organization requires a second step at sign-in. Scan the code below with an authenticator app, then enter the code it shows."
                          onDone={(result) => {
                            const session = result as { access_token: string; user: Parameters<typeof finishEnrolment>[1] }
                            finishEnrolment(session.access_token, session.user)
                          }}
                          onStartFailed={(reason) => {
                            setError(reason)
                            setPartialToken('')
                            setEmailStep('login')
                          }}
                        />
                      </div>

                      <div className="mt-4 text-center text-sm text-slate-400">
                        <button
                          className="font-medium text-amber-300 transition-colors hover:text-amber-200"
                          onClick={() => {
                            setEmailStep('login')
                            setError('')
                            setPartialToken('')
                            setTotpCode('')
                          }}
                          type="button"
                        >
                          Back to login
                        </button>
                      </div>

                      <div className="mt-5">
                        <SecurityStrip />
                      </div>
                    </div>
                  )}

                  {emailStep === '2fa' && (
                    <div className="login-panel">
                      <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-300/10 text-amber-100">
                          <Smartphone size={22} />
                        </div>
                        <div>
                          <div className="text-[11px] font-medium uppercase tracking-[0.34em] text-slate-400">Two-Factor Auth</div>
                          <h2 className="mt-1 text-2xl font-medium text-white">Enter your verification code</h2>
                        </div>
                      </div>

                      <div className="mt-5 space-y-4">
                        <InputField
                          autoComplete="one-time-code"
                          autoFocus
                          icon={Smartphone}
                          id="login-totp"
                          inputMode="numeric"
                          label="Authenticator Code"
                          maxLength={6}
                          onChange={(value) => setTotpCode(value.replace(/\D/g, '').slice(0, 6))}
                          onKeyDown={(event) => event.key === 'Enter' && totpCode.length === 6 && handle2FAVerify()}
                          placeholder="000000"
                          type="text"
                          value={totpCode}
                        />

                        {renderError()}

                        <div className="flex justify-center pt-1">
                          <button
                            className="login-action-gold"
                            disabled={loading || totpCode.length !== 6}
                            onClick={handle2FAVerify}
                            type="button"
                          >
                            {renderActionLabel('Verify & Log in', Shield)}
                          </button>
                        </div>

                        <div className="text-center text-sm text-slate-400">
                          Need to restart?{' '}
                          <button
                            className="font-medium text-amber-300 transition-colors hover:text-amber-200"
                            onClick={() => {
                              setEmailStep('login')
                              setError('')
                              setPartialToken('')
                              setTotpCode('')
                            }}
                            type="button"
                          >
                            Back to login
                          </button>
                        </div>
                      </div>

                      <div className="mt-5">
                        <SecurityStrip />
                      </div>
                    </div>
                  )}
                </div>
              {SHOW_MICROSOFT_LOGIN_UI && (
                <div className="mt-6 login-panel">
                  <SecurityStrip />

                  <button
                    className="login-action mt-6"
                    onClick={handleMicrosoftLogin}
                    type="button"
                  >
                    <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                    </svg>
                    Continue with Microsoft
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
